import { useState, useEffect } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { gitlabApi, GitLabConnection, GitLabGroup } from '@/lib/api/gitlab';
import toast from 'react-hot-toast';
import { Gitlab, Loader2, Plus, Building, User, ExternalLink, AlertCircle, Download } from 'lucide-react';

interface GitLabModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  projectId: string;
  projectSlug: string;
  existingConnection?: {
    connectionId: string;
    repoUrl: string;
    repoOwner: string;
    repoName: string;
    defaultBranch: string;
    syncEnabled: boolean;
  };
}

type Step = 'select-connection' | 'configure' | 'confirm';

export default function GitLabModal({
  isOpen,
  onClose,
  onSuccess,
  projectId,
  projectSlug,
  existingConnection,
}: GitLabModalProps) {
  const { getIdToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [step, setStep] = useState<Step>('select-connection');

  // Connection state
  const [connections, setConnections] = useState<GitLabConnection[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string>('');
  const [selectedConnection, setSelectedConnection] = useState<GitLabConnection | null>(null);

  // Config state
  const [groups, setGroups] = useState<GitLabGroup[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<string>('');
  const [repoName, setRepoName] = useState(projectSlug);
  const [branch, setBranch] = useState('main');
  const [visibility, setVisibility] = useState<'private' | 'internal' | 'public'>('private');
  const [createRepo, setCreateRepo] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadConnections();
      setError(null);
      if (existingConnection) {
        setSelectedConnectionId(existingConnection.connectionId);
        setSelectedOwner(existingConnection.repoOwner);
        setRepoName(existingConnection.repoName);
        setBranch(existingConnection.defaultBranch);
        setCreateRepo(false);
      }
    }
  }, [isOpen, existingConnection]);

  useEffect(() => {
    if (selectedConnectionId) {
      const conn = connections.find((c) => c.id === selectedConnectionId);
      setSelectedConnection(conn || null);
      if (conn) {
        loadGroups(selectedConnectionId);
        setSelectedOwner(conn.metadata?.username || '');
      }
    }
  }, [selectedConnectionId, connections]);

  const loadConnections = async () => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const data = await gitlabApi.listConnections(token);
      setConnections(data);
      if (data.length > 0 && !selectedConnectionId) {
        setSelectedConnectionId(data[0].id);
      }
    } catch (error) {
      console.error('Failed to load GitLab connections', error);
    }
  };

  const loadGroups = async (connectionId: string) => {
    try {
      const token = await getIdToken();
      if (!token) return;
      const data = await gitlabApi.listGroups(token, connectionId);
      setGroups(data);
    } catch (error) {
      console.error('Failed to load groups', error);
    }
  };

  const handleConnectGitLab = async () => {
    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Please sign in to continue');
        return;
      }
      const { authUrl } = await gitlabApi.initiateOAuth(token, undefined, projectId);
      window.location.href = authUrl;
    } catch (error) {
      console.error('Failed to initiate GitLab OAuth', error);
      toast.error('Failed to connect GitLab');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedConnectionId || !selectedOwner || !repoName) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const token = await getIdToken();
      if (!token) {
        setError('Please sign in to continue');
        return;
      }

      // Find namespace ID for group
      const selectedGroup = groups.find((g) => g.full_path === selectedOwner);

      await gitlabApi.connectProject(token, projectId, {
        connectionId: selectedConnectionId,
        owner: selectedOwner,
        repoName,
        branch,
        visibility,
        createRepo,
        namespaceId: selectedGroup ? String(selectedGroup.id) : undefined,
      });
      toast.success('Project connected to GitLab!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to connect project to GitLab', error);
      setError(error.response?.data?.message || 'Failed to connect to GitLab');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      setLoading(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Please sign in to continue');
        return;
      }
      await gitlabApi.disconnectProject(token, projectId);
      toast.success('Disconnected from GitLab');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to disconnect from GitLab', error);
      toast.error('Failed to disconnect');
    } finally {
      setLoading(false);
    }
  };

  const handlePull = async () => {
    try {
      setPulling(true);
      const token = await getIdToken();
      if (!token) {
        toast.error('Please sign in to continue');
        return;
      }
      const result = await gitlabApi.pullFromGitlab(token, projectId);
      if (result.success) {
        toast.success('Pulled latest changes from GitLab');
      } else {
        toast.error(result.error || 'Failed to pull from GitLab');
      }
    } catch (error: any) {
      console.error('Failed to pull from GitLab', error);
      toast.error(error.response?.data?.message || 'Failed to pull from GitLab');
    } finally {
      setPulling(false);
    }
  };

  const renderSelectConnection = () => (
    <div className="space-y-4">
      {connections.length === 0 ? (
        <div className="text-center py-8">
          <Gitlab className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-6">No GitLab accounts connected</p>

          <Button onClick={handleConnectGitLab} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Gitlab className="h-4 w-4 mr-2" />}
            Connect GitLab Account
          </Button>
        </div>
      ) : (
        <>
          <div className="space-y-2">
            <Label>GitLab Account</Label>
            <Select value={selectedConnectionId} onValueChange={setSelectedConnectionId}>
              <SelectTrigger>
                <SelectValue placeholder="Select GitLab account" />
              </SelectTrigger>
              <SelectContent>
                {connections.map((conn) => (
                  <SelectItem key={conn.id} value={conn.id}>
                    <div className="flex items-center gap-2">
                      <Avatar className="h-5 w-5">
                        <AvatarImage src={conn.metadata?.avatar_url} />
                        <AvatarFallback>{conn.metadata?.username?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <span>{conn.metadata?.username || conn.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button variant="outline" size="sm" onClick={handleConnectGitLab} disabled={loading}>
            <Plus className="h-4 w-4 mr-2" />
            Add another account
          </Button>

          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={() => setStep('configure')} disabled={!selectedConnectionId}>
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  );

  const renderConfigure = () => (
    <div className="space-y-4">
      {/* Owner selection */}
      <div className="space-y-2">
        <Label>Repository Owner</Label>
        <Select value={selectedOwner} onValueChange={setSelectedOwner}>
          <SelectTrigger>
            <SelectValue placeholder="Select owner" />
          </SelectTrigger>
          <SelectContent>
            {/* Personal account */}
            {selectedConnection?.metadata?.username && (
              <SelectItem value={selectedConnection.metadata.username}>
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  <span>{selectedConnection.metadata.username}</span>
                  <span className="text-muted-foreground text-xs">(Personal)</span>
                </div>
              </SelectItem>
            )}
            {/* Groups */}
            {groups.map((group) => (
              <SelectItem key={group.id} value={group.full_path}>
                <div className="flex items-center gap-2">
                  <Avatar className="h-4 w-4">
                    <AvatarImage src={group.avatar_url} />
                    <AvatarFallback><Building className="h-3 w-3" /></AvatarFallback>
                  </Avatar>
                  <span>{group.full_path}</span>
                </div>
              </SelectItem>
            ))}
            {/* Add another account */}
            <SelectSeparator />
            <div
              className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-2 pr-8 text-sm outline-none hover:bg-accent focus:bg-accent"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleConnectGitLab();
              }}
            >
              <div className="flex items-center gap-2 text-muted-foreground">
                <Plus className="h-4 w-4" />
                <span>Add GitLab Account</span>
              </div>
            </div>
          </SelectContent>
        </Select>
      </div>

      {/* Repository name */}
      <div className="space-y-2">
        <Label>Repository Name</Label>
        <Input
          value={repoName}
          onChange={(e) => setRepoName(e.target.value)}
          placeholder="my-project"
        />
      </div>

      {/* Branch */}
      <div className="space-y-2">
        <Label>Branch</Label>
        <Input
          value={branch}
          onChange={(e) => setBranch(e.target.value)}
          placeholder="main"
        />
      </div>

      {/* Create new repo toggle */}
      {!existingConnection && (
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Create new repository</Label>
            <p className="text-sm text-muted-foreground">
              Create a new repo or push to existing
            </p>
          </div>
          <input
            type="checkbox"
            checked={createRepo}
            onChange={(e) => setCreateRepo(e.target.checked)}
            className="h-4 w-4"
          />
        </div>
      )}

      {/* Visibility */}
      {createRepo && (
        <div className="space-y-2">
          <Label>Visibility</Label>
          <Select value={visibility} onValueChange={(v: 'private' | 'internal' | 'public') => setVisibility(v)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="private">Private</SelectItem>
              <SelectItem value="internal">Internal</SelectItem>
              <SelectItem value="public">Public</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="flex justify-between gap-2 pt-4">
        <Button variant="outline" onClick={() => setStep('select-connection')}>
          Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => { setStep('confirm'); setError(null); }} disabled={!selectedOwner || !repoName}>
            Next
          </Button>
        </div>
      </div>
    </div>
  );

  const renderConfirm = () => (
    <div className="space-y-4">
      <div className="bg-muted p-4 rounded-lg space-y-2">
        <p className="text-sm">
          Your code will sync to GitLab ({selectedOwner}).
        </p>
        <p className="text-sm text-muted-foreground">
          You can disconnect anytime in Project Settings.
        </p>
      </div>

      <div className="bg-card border rounded-lg p-4 space-y-2">
        <div className="flex items-center gap-2 text-sm">
          <Gitlab className="h-4 w-4" />
          <span className="font-medium">{selectedOwner}/{repoName}</span>
        </div>
        <div className="text-sm text-muted-foreground">
          Branch: {branch}
        </div>
        {createRepo && (
          <div className="text-sm text-muted-foreground">
            Visibility: {visibility}
          </div>
        )}
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-3">
          <div className="flex gap-2">
            <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        </div>
      )}

      <div className="flex justify-between gap-2 pt-4">
        <Button variant="outline" onClick={() => { setStep('configure'); setError(null); }}>
          Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading} variant="destructive">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Transfer anyway
          </Button>
        </div>
      </div>
    </div>
  );

  const renderExistingConnection = () => (
    <div className="space-y-4">
      <div className="bg-card border rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Gitlab className="h-5 w-5" />
            <span className="font-medium">Connected to GitLab</span>
          </div>
          <a
            href={existingConnection?.repoUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary hover:underline flex items-center gap-1"
          >
            View on GitLab
            <ExternalLink className="h-3 w-3" />
          </a>
        </div>
        <div className="text-sm text-muted-foreground">
          {existingConnection?.repoOwner}/{existingConnection?.repoName}
        </div>
        <div className="text-sm text-muted-foreground">
          Branch: {existingConnection?.defaultBranch}
        </div>
      </div>

      <div className="flex justify-between gap-2 pt-4">
        <Button variant="outline" onClick={handleDisconnect} disabled={loading || pulling}>
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
          Disconnect
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePull} disabled={loading || pulling}>
            {pulling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Pull
          </Button>
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md border-border">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gitlab className="h-5 w-5" />
            GitLab
          </DialogTitle>
          <DialogDescription>
            {existingConnection
              ? 'Manage your GitLab connection'
              : 'Sync your project 2-way with GitLab to collaborate at source.'}
          </DialogDescription>
        </DialogHeader>

        {existingConnection ? (
          renderExistingConnection()
        ) : (
          <>
            {step === 'select-connection' && renderSelectConnection()}
            {step === 'configure' && renderConfigure()}
            {step === 'confirm' && renderConfirm()}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
